import { useEffect, useState } from "react";
import {
  CONSENT_CATEGORIES,
  NON_ESSENTIAL_CATEGORIES,
} from "@/shared/lib/consent/categories";
import { useConsent } from "@/shared/lib/consent/useConsent";
import type { ConsentCategory } from "@/shared/lib/consent/types";
import { Button } from "@/shared/components/ui/Button";
import { Checkbox } from "@/shared/components/ui/Checkbox";
import { cn } from "@/shared/lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/Dialog";

interface ConsentManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Draft = Record<ConsentCategory, boolean>;

/** Seed the toggle draft from current consent (deny non-essential by default). */
function draftFromSnapshot(
  isGranted: (category: ConsentCategory) => boolean,
): Draft {
  const draft = { essential: true } as Draft;
  for (const category of NON_ESSENTIAL_CATEGORIES) {
    draft[category] = isGranted(category);
  }
  return draft;
}

/**
 * The "Customize" modal: one toggle per consent category. Reused by the banner
 * and by `CookieSettingsButton` so visitors can revisit their choices. The
 * essential row is always checked + disabled.
 */
export function ConsentManager({ open, onOpenChange }: ConsentManagerProps) {
  const { isGranted, setCategories, acceptAll } = useConsent();
  const [draft, setDraft] = useState<Draft>(() => draftFromSnapshot(isGranted));

  // Re-seed from current consent each time the modal opens so it reflects any
  // change made elsewhere (e.g. another tab) since it last closed. Keyed on
  // `open` only — `isGranted` is recreated each render, so depending on it
  // would re-seed mid-edit and clobber the user's in-progress toggles.
  useEffect(() => {
    if (open) setDraft(draftFromSnapshot(isGranted));
  }, [open]);

  function handleSave() {
    setCategories(draft);
    onOpenChange(false);
  }

  function handleAcceptAll() {
    acceptAll();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="gap-5">
        <DialogHeader>
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            Choose which cookies we may use. Essential cookies are always on.
            Turning a category off prevents new tracking; already-loaded scripts
            clear on your next page reload.
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col rounded-xl border border-border-muted glass-surface-subtle">
          {CONSENT_CATEGORIES.map((category) => (
            <li key={category.id}>
              <label
                className={cn(
                  "flex items-start gap-3 border-t border-border-muted px-4 py-3 first:border-t-0",
                  // Essential rows aren't interactive; keep the cursor neutral.
                  category.essential ? "cursor-default" : "cursor-pointer",
                )}
              >
                <Checkbox
                  className="mt-0.5"
                  checked={category.essential ? true : draft[category.id]}
                  disabled={category.essential}
                  onCheckedChange={(value) =>
                    setDraft((prev) => ({
                      ...prev,
                      [category.id]: value === true,
                    }))
                  }
                  aria-label={category.label}
                />
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="type-label-base tracking-wider uppercase text-text">
                    {category.label}
                  </span>
                  <span className="type-body-sm text-text-muted">
                    {category.description}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button onClick={handleSave}>Save preferences</Button>
          <Button variant="secondary" onClick={handleAcceptAll}>
            Accept all
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
